import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  BarList, Button, Card, Select, SelectItem, Tab, TabGroup, TabList, Text, Title,
} from '@tremor/react';
import { fetchSalespeople, fetchCounterstaff, fetchStaffLines, fetchStaffLostByLine } from '../api/salespeople';
import AccessDenied from '../components/AccessDenied';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import DateRangePresets from '../components/DateRangePresets';
import PrintHeader from '../components/PrintHeader';
import { exportReportPdf } from '../lib/pdf';
import StaffLinesTable from '../components/StaffLinesTable';
import LostByLineSection from '../components/LostByLineSection';
import { currentUser } from '../api/auth';
import { canSeeMargin } from '../lib/roles';
import { useStoreFilter } from '../lib/storeFilter';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type {
  StaffAttribution, StaffLineRow, StaffRow, StaffWindow, User, LostByLineRow,
} from '../api/types';
import Header from '../components/Header';
import SalespeopleTable from '../components/SalespeopleTable';
import StaffTopPartsModal from '../components/StaffTopPartsModal';

const WINDOWS: StaffWindow[] = ['mtd', 'd30', 'd7'];
const ATTRIBUTIONS: StaffAttribution[] = ['slid', 'ctrmn'];
type ViewMode = 'resumen' | 'porLinea' | 'perdidas';
const VIEWS: ViewMode[] = ['resumen', 'porLinea', 'perdidas'];

const fmtMxn = (n: number) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', minimumFractionDigits: 0,
  }).format(n);

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

/** CSV field escaper: quotes fields that contain commas, double-quotes, or newlines. */
function csvEsc(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function Salespeople() {
  // Tri-state currentUser load: undefined = pending; null = redirect; User = render.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <SalespeopleInner user={user} /> : <Navigate to="/login" replace />;
}

function SalespeopleInner({ user }: { user: User }) {
  const isAuthorized = user.role === 'owner' || user.role === 'manager';

  const [attribution, setAttribution] = useState<StaffAttribution>('slid');
  const [win, setWin] = useState<StaffWindow>('d30');
  // Shared header store (lib/storeFilter) — persists across pages.
  const [sid] = useStoreFilter(user);
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<StaffRow | null>(null);

  // v2.45 — "Por línea" view: a custom date range + per-line breakdown.
  const [view, setView] = useState<ViewMode>('resumen');
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [lineRows, setLineRows] = useState<StaffLineRow[] | null>(null);
  const [lineErr, setLineErr] = useState<string | null>(null);

  // v2.56 — "Ventas perdidas por línea" view (lost sales = titm type "L").
  const [lostRows, setLostRows] = useState<LostByLineRow[] | null>(null);
  const [lostErr, setLostErr] = useState<string | null>(null);

  // Person selector: '' = Todos.
  const [selectedPerson, setSelectedPerson] = useState('');

  // CSV export controls — Por-línea view only.
  const [includeLineSubtotals, setIncludeLineSubtotals] = useState(false);

  // Reset person selector whenever attribution or view changes.
  useEffect(() => { setSelectedPerson(''); }, [attribution, view]);

  // Fetch the staff leaderboard whenever the attribution / window / store filter changes.
  const [loading, setLoading] = useState(false);
  const load = () => {
    if (!isAuthorized) return;
    let alive = true;
    setErr(null);
    setLoading(true);
    const fn = attribution === 'slid' ? fetchSalespeople : fetchCounterstaff;
    fn({ win, sid: sid === null ? undefined : sid })
      .then((r) => { if (alive) setRows(r); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };
  useEffect(() => { load(); }, [isAuthorized, attribution, win, sid]);

  // Fetch the per-line breakdown whenever the Por-línea view is active and its
  // inputs change. Live daemon call honoring the chosen date range.
  const loadLines = () => {
    if (!isAuthorized || view !== 'porLinea') return;
    let alive = true;
    setLineErr(null);
    setLineRows(null);
    fetchStaffLines({ attribution, from, to, sid: sid === null ? undefined : sid })
      .then((r) => { if (alive) setLineRows(r); })
      .catch((e: Error) => { if (alive) setLineErr(e.message ?? String(e)); });
    return () => { alive = false; };
  };
  useEffect(() => { loadLines(); }, [isAuthorized, view, attribution, from, to, sid]);

  // Fetch lost-sales-by-line only when the Perdidas view is active (it's a
  // heavy daemon scan; lazy keeps the page snappy on the other views).
  const loadLost = () => {
    if (!isAuthorized || view !== 'perdidas') return;
    let alive = true;
    setLostErr(null);
    setLostRows(null);
    fetchStaffLostByLine({ attribution, from, to, sid: sid === null ? undefined : sid })
      .then((r) => { if (alive) setLostRows(r); })
      .catch((e: Error) => { if (alive) setLostErr(e.message ?? String(e)); });
    return () => { alive = false; };
  };
  useEffect(() => { loadLost(); }, [isAuthorized, view, attribution, from, to, sid]);

  // Person dropdown options — derived from the active view's dataset.
  const personOptions = useMemo<{ value: string; label: string }[]>(() => {
    if (view === 'resumen') {
      const seen = new Set<string>();
      return rows
        .filter((r) => !r.is_unassigned && !seen.has(r.staff_id) && (seen.add(r.staff_id), true))
        .map((r) => ({ value: r.staff_id, label: r.name }));
    }
    if (view === 'perdidas') {
      const seen = new Set<string>();
      return (lostRows ?? [])
        .filter((r) => r.person_id !== '' && !seen.has(r.person_id) && (seen.add(r.person_id), true))
        .map((r) => ({ value: r.person_id, label: r.person_name }));
    }
    // porLinea
    const seen = new Set<string>();
    return (lineRows ?? [])
      .filter((r) => !seen.has(r.id) && (seen.add(r.id), true))
      .map((r) => ({ value: r.id, label: r.name }));
  }, [view, rows, lineRows, lostRows]);

  // Filtered datasets used for display.
  const filteredRows = useMemo(
    () => (selectedPerson ? rows.filter((r) => r.staff_id === selectedPerson) : rows),
    [rows, selectedPerson],
  );
  const filteredLineRows = useMemo(
    () => (selectedPerson ? (lineRows ?? []).filter((r) => r.id === selectedPerson) : (lineRows ?? [])),
    [lineRows, selectedPerson],
  );
  const filteredLostRows = useMemo(
    () => (selectedPerson ? (lostRows ?? []).filter((r) => r.person_id === selectedPerson) : (lostRows ?? [])),
    [lostRows, selectedPerson],
  );

  // Resumen chart — top 10 by revenue, always full ranking (ignores person filter).
  const byPersonBars = useMemo(
    () =>
      [...rows]
        .filter((r) => !r.is_unassigned)
        .sort((a, b) => b.revenue_mxn - a.revenue_mxn)
        .slice(0, 10)
        .map((r) => ({ name: r.name, value: r.revenue_mxn })),
    [rows],
  );

  // Por-línea chart — revenue by line, aggregated from the filtered lineRows, top 12 desc.
  const byLineBars = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of filteredLineRows) {
      m.set(r.line, (m.get(r.line) ?? 0) + r.revenue_mxn);
    }
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([name, value]) => ({ name, value }));
  }, [filteredLineRows]);

  // Build and trigger download of the commissions CSV from filteredLineRows.
  function downloadCsv() {
    if (filteredLineRows.length === 0) return;

    const showMargin = canSeeMargin(user);
    const attrLabel = t.salespeople.attr[attribution];
    const cols = t.salespeople.byLine.cols;
    const exportT = t.salespeople.export;

    // Group rows by person id, summing per-person totals.
    const personMap = new Map<string, { name: string; lines: StaffLineRow[]; units: number; revenue: number; margin: number }>();
    for (const r of filteredLineRows) {
      let g = personMap.get(r.id);
      if (!g) {
        g = { name: r.name, lines: [], units: 0, revenue: 0, margin: 0 };
        personMap.set(r.id, g);
      }
      g.lines.push(r);
      g.units += r.units;
      g.revenue += r.revenue_mxn;
      g.margin += r.margin_mxn ?? 0;
    }

    // Sort people by total revenue desc; sort each person's lines by revenue desc.
    const groups = [...personMap.values()].sort((a, b) => b.revenue - a.revenue);
    groups.forEach((g) => g.lines.sort((a, b) => b.revenue_mxn - a.revenue_mxn));

    const csvRows: string[] = [];

    if (includeLineSubtotals) {
      // Header: [attr.label, 'Línea', 'Unidades', 'Venta'[, 'Margen']]
      const header: (string | number)[] = [attrLabel, cols.line, cols.units, cols.revenue];
      if (showMargin) header.push(cols.margin);
      csvRows.push(header.map(csvEsc).join(','));

      for (const g of groups) {
        for (const l of g.lines) {
          const row: (string | number)[] = [g.name, l.line, l.units, l.revenue_mxn];
          if (showMargin) row.push(l.margin_mxn ?? 0);
          csvRows.push(row.map(csvEsc).join(','));
        }
        // Person total row
        const totRow: (string | number)[] = [g.name, exportT.total, g.units, g.revenue];
        if (showMargin) totRow.push(g.margin);
        csvRows.push(totRow.map(csvEsc).join(','));
      }
    } else {
      // Header: [attr.label, 'Unidades', 'Venta'[, 'Margen']]
      const header: (string | number)[] = [attrLabel, cols.units, cols.revenue];
      if (showMargin) header.push(cols.margin);
      csvRows.push(header.map(csvEsc).join(','));

      for (const g of groups) {
        const row: (string | number)[] = [g.name, g.units, g.revenue];
        if (showMargin) row.push(g.margin);
        csvRows.push(row.map(csvEsc).join(','));
      }
    }

    const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comisiones_${attribution}_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Generate the commission report as a branded PDF (header + date range +
  // who printed + timestamp). Same grouping as downloadCsv; amounts are
  // MXN-formatted and per-person total rows are emphasized.
  function downloadPdf() {
    if (filteredLineRows.length === 0) return;

    const showMargin = canSeeMargin(user);
    const attrLabel = t.salespeople.attr[attribution];
    const cols = t.salespeople.byLine.cols;
    const exportT = t.salespeople.export;
    const money = (n: number) =>
      new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

    const personMap = new Map<string, { name: string; lines: StaffLineRow[]; units: number; revenue: number; margin: number }>();
    for (const r of filteredLineRows) {
      let g = personMap.get(r.id);
      if (!g) { g = { name: r.name, lines: [], units: 0, revenue: 0, margin: 0 }; personMap.set(r.id, g); }
      g.lines.push(r);
      g.units += r.units;
      g.revenue += r.revenue_mxn;
      g.margin += r.margin_mxn ?? 0;
    }
    const groups = [...personMap.values()].sort((a, b) => b.revenue - a.revenue);
    groups.forEach((g) => g.lines.sort((a, b) => b.revenue_mxn - a.revenue_mxn));

    const rows: Array<Array<string | number>> = [];
    const emphasize: number[] = [];
    let columns: string[];
    let numericColumns: number[];

    if (includeLineSubtotals) {
      columns = showMargin
        ? [attrLabel, cols.line, cols.units, cols.revenue, cols.margin]
        : [attrLabel, cols.line, cols.units, cols.revenue];
      for (const g of groups) {
        for (const l of g.lines) {
          const row: Array<string | number> = [g.name, l.line, l.units, money(l.revenue_mxn)];
          if (showMargin) row.push(money(l.margin_mxn ?? 0));
          rows.push(row);
        }
        emphasize.push(rows.length);
        const tot: Array<string | number> = [g.name, exportT.total, g.units, money(g.revenue)];
        if (showMargin) tot.push(money(g.margin));
        rows.push(tot);
      }
      numericColumns = showMargin ? [2, 3, 4] : [2, 3];
    } else {
      columns = showMargin
        ? [attrLabel, cols.units, cols.revenue, cols.margin]
        : [attrLabel, cols.units, cols.revenue];
      for (const g of groups) {
        const row: Array<string | number> = [g.name, g.units, money(g.revenue)];
        if (showMargin) row.push(money(g.margin));
        rows.push(row);
      }
      numericColumns = showMargin ? [1, 2, 3] : [1, 2];
    }

    exportReportPdf({
      title: exportT.printTitle,
      meta: [`${attrLabel} · ${from} – ${to}`],
      printedBy: user.username,
      columns,
      rows,
      numericColumns,
      emphasizeRows: emphasize,
      filename: `comisiones_${attribution}_${from}_${to}.pdf`,
    });
  }

  const attrIdx = ATTRIBUTIONS.indexOf(attribution);
  const winIdx = WINDOWS.indexOf(win);
  const viewIdx = VIEWS.indexOf(view);

  // Print subtitle for Por-línea: attribution label + date range.
  const printSubtitle = `${t.salespeople.attr[attribution]} · ${from} – ${to}`;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} storeFilter={isAuthorized} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <Title>{t.salespeople.title}</Title>
        <Text className="text-tremor-content text-sm">{t.salespeople.subtitle}</Text>

        {!isAuthorized ? (
          <AccessDenied user={user} />
        ) : (
          <>
            <TabGroup index={attrIdx} onIndexChange={(i) => setAttribution(ATTRIBUTIONS[i])}>
              <TabList variant="solid">
                {ATTRIBUTIONS.map((a) => (
                  <Tab key={a}>{t.salespeople.attr[a]}</Tab>
                ))}
              </TabList>
            </TabGroup>

            <TabGroup index={viewIdx} onIndexChange={(i) => setView(VIEWS[i])}>
              <TabList>
                {VIEWS.map((v) => (
                  <Tab key={v}>{t.salespeople.view[v]}</Tab>
                ))}
              </TabList>
            </TabGroup>

            {/* Person selector — visible in both views */}
            <div className="flex items-center gap-2">
              <Text className="text-sm shrink-0">{t.salespeople.personFilter.label}</Text>
              <Select
                value={selectedPerson || '__all__'}
                onValueChange={(v) => setSelectedPerson(v === '__all__' ? '' : v)}
                className="w-56"
                aria-label={t.salespeople.personFilter.label}
              >
                <SelectItem value="__all__">{t.salespeople.personFilter.all}</SelectItem>
                {personOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </Select>
            </div>

            {view === 'resumen' ? (
              <>
                <TabGroup index={winIdx} onIndexChange={(i) => setWin(WINDOWS[i])}>
                  <TabList>
                    {WINDOWS.map((w) => (
                      <Tab key={w}>{t.salespeople.win[w]}</Tab>
                    ))}
                  </TabList>
                </TabGroup>

                {err ? (
                  <ErrorRetry message={err} onRetry={load} />
                ) : loading && rows.length === 0 ? (
                  <TableSkeleton rows={6} cols={5} />
                ) : (
                  <>
                    {byPersonBars.length > 0 && (
                      <Card>
                        <Title className="text-base">{t.salespeople.charts.byPerson}</Title>
                        <BarList
                          data={byPersonBars}
                          valueFormatter={fmtMxn}
                          className="mt-3"
                        />
                      </Card>
                    )}
                    <SalespeopleTable
                      rows={filteredRows}
                      onRowClick={(r) => {
                        setSelected(r);
                        setOpen(true);
                      }}
                    />
                  </>
                )}
              </>
            ) : view === 'porLinea' ? (
              <>
                {/* Por-línea toolbar (no-print) */}
                <Card className="no-print">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
                    <label className="flex items-center gap-1.5 text-sm text-tremor-content cursor-pointer select-none sm:ml-auto">
                      <input
                        type="checkbox"
                        className="rounded border-tremor-border"
                        checked={includeLineSubtotals}
                        onChange={(e) => setIncludeLineSubtotals(e.target.checked)}
                      />
                      {t.salespeople.export.includeLines}
                    </label>
                    <Button
                      size="xs"
                      variant="secondary"
                      disabled={filteredLineRows.length === 0}
                      onClick={downloadCsv}
                    >
                      {t.salespeople.export.button}
                    </Button>
                    <Button
                      size="xs"
                      variant="secondary"
                      disabled={filteredLineRows.length === 0}
                      onClick={downloadPdf}
                    >
                      {t.salespeople.export.pdfButton}
                    </Button>
                  </div>
                </Card>

                {lineErr ? (
                  <ErrorRetry message={lineErr} onRetry={loadLines} />
                ) : lineRows === null ? (
                  <TableSkeleton rows={6} cols={4} />
                ) : (
                  // print-region: wraps the by-línea chart + table.
                  // The toolbar Card above carries no-print and is outside.
                  <div className="print-region space-y-4">
                    <PrintHeader
                      title={t.salespeople.export.printTitle}
                      subtitle={printSubtitle}
                    />
                    {byLineBars.length > 0 && (
                      <Card>
                        <Title className="text-base">{t.salespeople.charts.byLine}</Title>
                        <BarList
                          data={byLineBars}
                          valueFormatter={fmtMxn}
                          className="mt-3"
                        />
                      </Card>
                    )}
                    <StaffLinesTable rows={filteredLineRows} showMargin={canSeeMargin(user)} />
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Perdidas (lost-sales) toolbar — date range */}
                <Card className="no-print">
                  <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
                </Card>
                {lostErr ? (
                  <ErrorRetry message={lostErr} onRetry={loadLost} />
                ) : lostRows === null ? (
                  <TableSkeleton rows={6} cols={5} />
                ) : (
                  <LostByLineSection rows={filteredLostRows} />
                )}
              </>
            )}

            <StaffTopPartsModal
              open={open}
              row={selected}
              attribution={attribution}
              win={win}
              onClose={() => setOpen(false)}
            />
          </>
        )}
      </main>
    </div>
  );
}
